import { extractLink } from "@theme/components";

import type { StrapiLinkInterface } from "@ms/gatsby-site/src/types/strapi";
import type { MouseEventHandler } from "react";

// BaseUrl needed for some blog links
export const baseUrl =
  process.env.MS_ENVIRONMENT === "staging"
    ? "https://stg.us.manscaped.io"
    : "https://www.manscaped.com";

/**
 * Get Internal or External Link
 *
 * Some areas, like the navigation featured information, may rarely link externally
 * We need to determine whether to append the base url to that link for blog support
 *
 * @param {string | null} slug
 * @param {boolean} isBlog
 * @returns {string | null} link
 */
export const getInternalOrExternalLink = (slug?: string | null, isBlog = false) => {
  if (slug) {
    const internal = slug.startsWith("/");
    if (internal) {
      return isBlog ? `${baseUrl}${slug}` : slug;
    } else {
      return slug;
    }
  }

  return null;
};

/**
 * Returns the base URL for the current market based on the `MS_MARKET` environment variable.
 *
 * - If `MS_MARKET` is `"US"`, returns `https://www.manscaped.com`.
 * - Otherwise, returns `https://{market}.manscaped.com`, where `{market}` is the lowercase value of `MS_MARKET`.
 *
 * @returns {string} The market-specific Manscaped URL.
 */
export const getMarketUrl = (slug?: `/${string}`) =>
  `https://${
    process.env.MS_MARKET === "US" ? "www" : process.env.MS_MARKET?.toLocaleLowerCase()
  }.manscaped.com${slug ?? ""}`;

/**
 * Is Link Active
 *
 * This function checks if a link is active by comparing the pathname to the link's path.
 *
 * @param {StrapiLinkInterface | { to?: string }} link
 * @param {string} pathname
 * @returns {boolean}
 */
export const isLinkActive = (
  link: StrapiLinkInterface | { to?: string | MouseEventHandler<HTMLButtonElement> },
  pathname: string,
) => {
  const normalizePath = (path: string) =>
    path
      .split("?")[0] // Remove query parameters
      .replace(/^\/[a-z]{2}(-[a-z]{2})?\//, "/") // Remove language prefix (e.g., /fr/ or /fr-fr/)
      .replace(/\/$/, "") // Remove trailing slash
      .toLowerCase() // Normalize case
      .replace(/%20/g, "-") // Convert URL encoded spaces to hyphens
      .trim(); // Remove any leading/trailing whitespace

  if ("url" in link || "templatePage" in link || "templateLegal" in link) {
    const extractedPath = extractLink(link as StrapiLinkInterface);
    if (!extractedPath) return false;

    const cleanPathname = normalizePath(pathname);
    const cleanExtractedPath = normalizePath(extractedPath);

    return cleanPathname === cleanExtractedPath;
  }

  if (!("to" in link) || !link.to || typeof link.to !== "string") return false;

  const cleanPathname = normalizePath(pathname);
  const cleanToPath = normalizePath(link.to);

  return cleanPathname === cleanToPath;
};

/**
 * Get Product Link
 *
 * This function generates a product link with query parameters based on the provided handle and values.
 * Only some values are supported to preselect options in buy box - collection and size. If other keys are provided, they will be ignored.
 *
 * @param {string} handle - The product handle.
 * @param {Record<string, string>} values - The query parameters.
 * @returns {string} The product link.
 */
export const getProductLink = (
  handle: string,
  values: Record<string, string | null | undefined>,
): string => {
  const path = `/products/${encodeURIComponent(handle)}`;
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(values)) {
    const supportedKeys = ["size", "collection"];
    const isSupportedKey = supportedKeys.includes(key);
    if (isSupportedKey && value) {
      params.set(key, value);
    }
  }

  const search = params.toString();

  return search ? `${path}?${search}` : path;
};
